import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, ExternalLinkIcon, BriefcaseIcon, PlusIcon, BookOpenIcon } from 'lucide-react';
import { LeadProcessingDrawer, Lead } from '../../components/rental/LeadProcessingDrawer';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
// Mock Data Generator
const generateMockLeads = (): Lead[] => {
  const zones = ['Colentina', 'Floreasca', 'Pipera', 'Aviatorilor', 'Dorobanți', 'Primăverii', 'Herastrau', 'Baneasa', 'Cotroceni', 'Universitate', 'Romana', 'Unirii', 'Vitan', 'Titan', 'Pantelimon'];
  const sources: ('OLX' | 'Storia' | 'Imobiliare.ro')[] = ['OLX', 'Storia', 'Imobiliare.ro'];
  return Array.from({
    length: 15
  }, (_, i) => ({
    id: `RP${92216 + i}`,
    zone: zones[i % zones.length],
    rooms: Math.floor(Math.random() * 3) + 1,
    price: Math.floor(Math.random() * 300) + 300,
    phone: `0721${String(i).padStart(6, '0')}`,
    source: sources[i % sources.length],
    listingUrl: '#',
    currentStep: 1,
    verificationStatus: 'pending',
    callStatus: 'pending',
    isBlocked: false,
    isFinal: false,
    progress: '1/4'
  }));
};
export function ProspectingLists() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>(generateMockLeads());
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isGeneratingList, setIsGeneratingList] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'call' | 'collab' | 'portfolio' | 'blocked' | 'final'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Update Lead Handler
  const handleUpdateLead = (leadId: string, updates: Partial<Lead>) => {
    setLeads(prev => prev.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          ...updates
        };
      }
      return lead;
    }));
    // Update selected lead if open
    if (selectedLead?.id === leadId) {
      setSelectedLead(prev => prev ? {
        ...prev,
        ...updates
      } : null);
    }
  };
  // Handler for auto-switching filters
  const handleFilterChange = (newFilter: 'call' | 'collab' | 'portfolio' | 'all') => {
    setFilter(newFilter);
  };
  // Generate new prospecting list
  const handleRequestNewList = () => {
    setIsGeneratingList(true);
    // Simulate API call to generate new leads
    setTimeout(() => {
      const newLeads = generateMockLeads();
      setLeads([...leads, ...newLeads]);
      setIsGeneratingList(false);
      toast.success(`${newLeads.length} leaduri noi adăugate în listă!`);
    }, 1500);
  };
  // KPI Calculations
  const stats = {
    total: leads.length,
    toVerify: leads.filter(l => l.currentStep === 1 && !l.isFinal && !l.isBlocked).length,
    toCall: leads.filter(l => l.currentStep === 2 && !l.isFinal && !l.isBlocked || l.callStatus === 'nu-raspunde' && !l.isBlocked).length,
    collaborations: leads.filter(l => l.currentStep === 3 && l.postCallStatus === 'colaboreaza' && !l.propertyId && !l.isFinal && !l.isBlocked).length,
    portfolio: leads.filter(l => l.isFinal && l.propertyId && l.postCallStatus === 'colaboreaza').length,
    blocked: leads.filter(l => l.isBlocked && (l.postCallStatus && ['exclusivitate-alta', 'agentie', 'inchiriat-deja'].includes(l.postCallStatus) || l.verificationStatus === 'anunt-invalid')).length,
    validLeads: leads.filter(l => l.postCallStatus && ['colaboreaza', 'nu-colaboreaza', 'comision-redus'].includes(l.postCallStatus)).length
  };
  const conversionRate = stats.validLeads > 0 ? Math.round(stats.portfolio / stats.validLeads * 100) : 0;
  // Filter Logic
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.zone.toLowerCase().includes(searchQuery.toLowerCase()) || lead.phone.includes(searchQuery);
    if (!matchesSearch) return false;
    switch (filter) {
      case 'pending':
        // De Verificat: Step 1, not final, not blocked
        return lead.currentStep === 1 && !lead.isFinal && !lead.isBlocked;
      case 'call':
        // De Apelat: Step 2 OR status "nu-raspunde"
        return lead.currentStep === 2 && !lead.isFinal && !lead.isBlocked || lead.callStatus === 'nu-raspunde' && !lead.isBlocked;
      case 'collab':
        // Colaborări: Step 3, status "colaboreaza", NO propertyId yet
        return lead.currentStep === 3 && lead.postCallStatus === 'colaboreaza' && !lead.propertyId && !lead.isFinal && !lead.isBlocked;
      case 'portfolio':
        // Portofoliu: status "colaboreaza" AND propertyId exists
        return lead.isFinal && lead.propertyId && lead.postCallStatus === 'colaboreaza';
      case 'blocked':
        // Blocate: exclusivitate-alta, agentie, inchiriat-deja, anunt-invalid
        return lead.isBlocked && (lead.postCallStatus && ['exclusivitate-alta', 'agentie', 'inchiriat-deja'].includes(lead.postCallStatus) || lead.verificationStatus === 'anunt-invalid');
      case 'final':
        // Finalizate (old filter - might not be needed anymore)
        return lead.isFinal && !lead.isBlocked && !lead.propertyId;
      default:
        // Toate: show everything
        return true;
    }
  });
  const getStepBadge = (lead: Lead) => {
    if (lead.isBlocked) return <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold" data-id="element-10507">
          Blocat
        </span>;
    if (lead.isFinal && lead.propertyId) return <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center gap-1" data-id="element-10508">
          <BriefcaseIcon className="w-3 h-3" data-id="element-10509" />
          Portofoliu
        </span>;
    if (lead.isFinal) return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold" data-id="element-10510">
          Finalizat
        </span>;
    switch (lead.currentStep) {
      case 1:
        return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold" data-id="element-10511">
            Verificare
          </span>;
      case 2:
        return <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold" data-id="element-10512">
            Apel
          </span>;
      case 3:
        return <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold" data-id="element-10513">
            Colaborare
          </span>;
      default:
        return null;
    }
  };
  const getStatusBadge = (lead: Lead) => {
    // Show call status if exists
    if (lead.callStatus === 'nu-raspunde') {
      return <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium" data-id="element-10514">
          Nu răspunde
        </span>;
    }
    // Show post-call status if exists
    if (lead.postCallStatus) {
      const statusConfig: Record<string, {
        label: string;
        color: string;
      }> = {
        colaboreaza: {
          label: 'Colaborează',
          color: 'bg-green-100 text-green-700'
        },
        'nu-colaboreaza': {
          label: 'Nu colaborează',
          color: 'bg-red-100 text-red-700'
        },
        'comision-redus': {
          label: 'Comision redus',
          color: 'bg-yellow-100 text-yellow-700'
        },
        'inchiriat-deja': {
          label: 'Închiriat deja',
          color: 'bg-red-100 text-red-700'
        },
        'exclusivitate-alta': {
          label: 'Exclusivitate',
          color: 'bg-red-100 text-red-700'
        },
        'de-vanzare': {
          label: 'De vânzare',
          color: 'bg-blue-100 text-blue-700'
        },
        agentie: {
          label: 'Agenție',
          color: 'bg-slate-100 text-slate-700'
        }
      };
      const config = statusConfig[lead.postCallStatus] || {
        label: lead.postCallStatus,
        color: 'bg-slate-100 text-slate-600'
      };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`} data-id="element-10515">
          {config.label}
        </span>;
    }
    // Show verification status if exists
    if (lead.verificationStatus === 'de-prospectat') {
      return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium" data-id="element-10516">
          De prospectat
        </span>;
    }
    if (lead.verificationStatus === 'exista-colaborare') {
      return <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium" data-id="element-10517">
          Există colaborare
        </span>;
    }
    if (lead.verificationStatus === 'anunt-invalid') {
      return <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium" data-id="element-10518">
          Anunț invalid
        </span>;
    }
    // Default: no status yet
    return <span className="text-slate-300 text-xs" data-id="element-10519">—</span>;
  };
  return <div className="min-h-screen bg-slate-50 pb-20 font-roboto" data-id="element-10520">
      <Toaster position="top-right" data-id="element-10521" />

      {/* Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-16 pt-10" data-id="element-10522">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-10523" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-10524"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10525">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-10526">
            <div className="text-center md:text-left" data-id="element-10527">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-10528">
                Liste de{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-10529">
                  Prospectare
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-10530">
                Verifică → Apelează → Transformă în colaborare
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="flex items-center space-x-3" data-id="element-10531">
              <button onClick={() => navigate('/inchiriere/liste-prospectare/tutorial')} className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-10532">
                <BookOpenIcon className="w-5 h-5" data-id="element-10533" />
                <span data-id="element-10534">Tutorial</span>
              </button>
              <button onClick={handleRequestNewList} disabled={isGeneratingList} className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10535">
                {isGeneratingList ? <>
                    <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-10536" />
                    <span data-id="element-10537">Generare...</span>
                  </> : <>
                    <PlusIcon className="w-5 h-5" data-id="element-10538" />
                    <span data-id="element-10539">Solicită Listă</span>
                  </>}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-10540">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-id="element-10541">
          {[{
          label: 'Total Leaduri',
          value: stats.total,
          color: 'text-blue-600',
          bg: 'bg-blue-100'
        }, {
          label: 'De Verificat',
          value: stats.toVerify,
          color: 'text-blue-600',
          bg: 'bg-blue-100'
        }, {
          label: 'De Apelat',
          value: stats.toCall,
          color: 'text-purple-600',
          bg: 'bg-purple-100'
        }, {
          label: 'Colaborări',
          value: stats.collaborations,
          color: 'text-emerald-600',
          bg: 'bg-emerald-100'
        }, {
          label: 'Portofoliu',
          value: stats.portfolio,
          color: 'text-emerald-600',
          bg: 'bg-emerald-100'
        }, {
          label: 'Conversie',
          value: `${conversionRate}%`,
          color: 'text-blue-600',
          bg: 'bg-blue-100'
        }].map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-100" data-id="element-10542">
              <p className="text-sm text-slate-500 font-medium mb-1" data-id="element-10543">
                {stat.label}
              </p>
              <div className="flex items-center space-x-2" data-id="element-10544">
                <span className={`text-2xl sm:text-3xl font-bold ${stat.color}`} data-id="element-10545">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-10546">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50" data-id="element-10547">
            <div className="flex bg-slate-200/50 p-1 rounded-xl overflow-x-auto max-w-full" data-id="element-10548">
              <FilterTab label="Toate" active={filter === 'all'} onClick={() => setFilter('all')} data-id="element-10549" />
              <FilterTab label="De Verificat" active={filter === 'pending'} onClick={() => setFilter('pending')} count={stats.toVerify} data-id="element-10550" />
              <FilterTab label="De Apelat" active={filter === 'call'} onClick={() => setFilter('call')} count={stats.toCall} data-id="element-10551" />
              <FilterTab label="Colaborări" active={filter === 'collab'} onClick={() => setFilter('collab')} count={stats.collaborations} data-id="element-10552" />
              <FilterTab label="Portofoliu" active={filter === 'portfolio'} onClick={() => setFilter('portfolio')} count={stats.portfolio} icon={<BriefcaseIcon className="w-3 h-3" data-id="element-10554" />} data-id="element-10553" />
              <FilterTab label="Blocate" active={filter === 'blocked'} onClick={() => setFilter('blocked')} count={stats.blocked} data-id="element-10555" />
            </div>

            <div className="relative w-full sm:w-64" data-id="element-10556">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" data-id="element-10557" />
              <input type="text" placeholder="Caută zonă sau telefon..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#357BEB] focus:border-transparent outline-none" data-id="element-10558" />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-10559">
            <table className="w-full text-sm text-left" data-id="element-10560">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-10561">
                <tr data-id="element-10562">
                  <th className="px-4 py-4 text-xs" data-id="element-10563">ID</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10564">Zonă</th>
                  <th className="px-4 py-4 text-xs text-center" data-id="element-10565">Camere</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10566">Preț</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10567">Telefon</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10568">Sursă</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10569">Step</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10570">Status</th>
                  <th className="px-4 py-4 text-xs" data-id="element-10571">ID Proprietate</th>
                  <th className="px-4 py-4 text-xs text-center" data-id="element-10572">Progres</th>
                  <th className="px-4 py-4 text-xs text-right" data-id="element-10573">Acțiune</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-10574">
                {filteredLeads.map(lead => <motion.tr key={lead.id} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className={`hover:bg-slate-50 transition-colors ${lead.isBlocked ? 'bg-red-50/30' : lead.isFinal && lead.propertyId ? 'bg-emerald-50/20' : ''}`} data-id="element-10575">
                    <td className="px-4 py-3 font-mono text-slate-500 text-xs" data-id="element-10576">
                      {lead.id}
                    </td>
                    <td className="px-4 py-3 font-bold text-[#132C53] text-sm" data-id="element-10577">
                      {lead.zone}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-10578">
                      <span className="font-semibold text-slate-700" data-id="element-10579">
                        {lead.rooms}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-10580">
                      <span className="text-[#357BEB] font-bold text-sm whitespace-nowrap" data-id="element-10581">
                        {lead.price} €
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-600 text-xs" data-id="element-10582">
                      {lead.phone}
                    </td>
                    <td className="px-4 py-3" data-id="element-10583">
                      <div className="flex items-center gap-1.5" data-id="element-10584">
                        <span className="text-slate-600 text-xs" data-id="element-10585">
                          {lead.source}
                        </span>
                        <a href={lead.listingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600" data-id="element-10586">
                          <ExternalLinkIcon className="w-3 h-3" data-id="element-10587" />
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-10588">{getStepBadge(lead)}</td>
                    <td className="px-4 py-3" data-id="element-10589">{getStatusBadge(lead)}</td>
                    <td className="px-4 py-3" data-id="element-10590">
                      {lead.propertyId ? <span className="font-mono text-emerald-600 font-bold text-xs" data-id="element-10591">
                          {lead.propertyId}
                        </span> : lead.existingPropertyId ? <span className="font-mono text-orange-600 font-bold text-xs" data-id="element-10592">
                          {lead.existingPropertyId}
                        </span> : <span className="text-slate-300 text-xs" data-id="element-10593">—</span>}
                    </td>
                    <td className="px-4 py-3" data-id="element-10594">
                      <div className="flex items-center justify-center gap-2" data-id="element-10595">
                        <div className="w-12 bg-slate-200 h-1 rounded-full overflow-hidden" data-id="element-10596">
                          <div className={`h-full rounded-full ${lead.isBlocked ? 'bg-red-400' : lead.isFinal && lead.propertyId ? 'bg-emerald-500' : 'bg-[#357BEB]'}`} style={{
                        width: lead.progress === '1/4' ? '25%' : lead.progress === '2/4' ? '50%' : lead.progress === '3/4' ? '75%' : '100%'
                      }} data-id="element-10597"></div>
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono" data-id="element-10598">
                          {lead.isFinal && lead.propertyId ? '✓' : lead.progress}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-10599">
                      <button onClick={() => {
                    setSelectedLead(lead);
                    setIsDrawerOpen(true);
                  }} disabled={lead.isBlocked} className={`
                          px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm whitespace-nowrap
                          ${lead.isBlocked ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : lead.isFinal && lead.propertyId ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-[#357BEB] text-white hover:bg-[#2a62bc] hover:shadow-md'}
                        `} data-id="element-10600">
                        {lead.isFinal && lead.propertyId ? 'Vezi' : 'Prelucrează'}
                      </button>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <LeadProcessingDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} lead={selectedLead} onUpdate={handleUpdateLead} onFilterChange={handleFilterChange} data-id="element-10601" />
    </div>;
}
function KPICard({
  label,
  value,
  color,
  icon
}: {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}) {
  return <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100 flex flex-col justify-center" data-id="element-10602">
      <div className="flex items-center gap-2 mb-1" data-id="element-10603">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-10604">
          {label}
        </span>
        {icon && <span className={color} data-id="element-10605">{icon}</span>}
      </div>
      <span className={`text-3xl font-bold ${color}`} data-id="element-10606">{value}</span>
    </div>;
}
function FilterTab({
  label,
  active,
  onClick,
  count,
  icon
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
  icon?: React.ReactNode;
}) {
  return <button onClick={onClick} className={`
        px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2
        ${active ? 'bg-white text-[#132C53] shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'}
      `} data-id="element-10607">
      {icon}
      {label}
      {count !== undefined && <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-slate-100' : 'bg-slate-200'}`} data-id="element-10608">
          {count}
        </span>}
    </button>;
}